import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import PageHeader from "../Layouts/PageHeader";
import { Svg, UiIcon, SplitButton, CheckMark } from "../Layouts/Icon";
import {
  DUOTONE_OPACITY,
  ICON_VIEWBOX,
  gridPath,
  iconLabel,
  toSvg,
  toJsx,
  toDataUrl,
  downloadSvg,
  downloadPng,
} from "../utils/svgIcon";
import { copyTextToClipboard } from "../utils/formatters";
import { searchIcons } from "../utils/searchIcons";
import { iconStyle } from "../utils/iconStyle";
import { readCustomIcons, saveCustomIcon, onCustomIconsChange, MAX_CUSTOM_ICONS } from "../utils/customIcons";
import { COMPOSER_PATH, GLYPH_COUNT } from "../constants";
import { SHAPES, newLayer, describe, flatten, splitLayer, transformLayers, unionBox } from "./geometry";
import loadPaper from "./loadPaper";
import classes from "./studio.scss";

const DRAFT_KEY = "studio-draft";
const SLUG = /^[a-z][a-z0-9-]{0,39}$/;
const toSlug = (text) => text.toLowerCase().replace(/[\s_]+/g, "-").replace(/[^a-z0-9-]/g, "");
const EMPTY = { name: "my-icon", layers: [] };
const HISTORY_LIMIT = 100;
const ICON_RESULTS = 96;

// Small icons for the corner of another: added at 45% with a circle of space around them
const BADGES = ["plus-circle", "check-circled", "close-circled", "minus-circle", "exclamation-circle", "clock-time", "heart", "star", "lock"];

const EXPORTS = [
  { id: "copy-svg", label: "Copy SVG" },
  { id: "copy-jsx", label: "Copy JSX" },
  { id: "copy-data-url", label: "Copy Data URL" },
  { id: "download-svg", label: "Download SVG" },
  { id: "download-png", label: "Download PNG" },
];

const readDraft = () => {
  try {
    const draft = JSON.parse(window.localStorage.getItem(DRAFT_KEY));
    return draft && Array.isArray(draft.layers) ? { ...EMPTY, ...draft } : EMPTY;
  } catch (e) {
    return EMPTY;
  }
};

const saveDraft = (doc) => {
  try {
    window.localStorage.setItem(DRAFT_KEY, JSON.stringify(doc));
  } catch (e) {
    // Not kept this time; the design still works until the page closes
  }
};

const round = (value, step) => Math.round(value / step) * step;
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const isTyping = (target) =>
  target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);

/** The design as drawn: layers bottom to top, with the cut-outs from above as masks */
const Design = ({ described, idPrefix, editor }) => (
  <g>
    <defs>
      {described.map((item, index) =>
        item.cutBy.length ? (
          <mask key={index} id={`${idPrefix}-${index}`} maskUnits="userSpaceOnUse" x="-6" y="-6" width="36" height="36">
            <rect x="-6" y="-6" width="36" height="36" fill="#fff" />
            {item.cutBy.map((d, i) => (
              <path key={i} d={d} fill="#000" />
            ))}
          </mask>
        ) : null
      )}
    </defs>
    {described.map((item, index) => {
      const cutting = item.layer.mode === "cut";
      if (cutting && !editor) return null;
      return (
        <g key={item.layer.id} mask={item.cutBy.length ? `url(#${idPrefix}-${index})` : undefined}>
          {item.parts.map((part, i) => (
            <path
              key={i}
              d={part.d}
              // A cutting layer isn't part of the icon; the editor shows where it is
              className={cutting ? classes.cutPreview : undefined}
              opacity={cutting ? undefined : part.tone === "light" ? DUOTONE_OPACITY : 1}
            />
          ))}
        </g>
      );
    })}
  </g>
);

Design.propTypes = {
  described: PropTypes.array.isRequired,
  idPrefix: PropTypes.string.isRequired,
  editor: PropTypes.bool,
};

let previewCount = 0;

/** The finished design at a real size */
const Preview = ({ described, size }) => {
  const id = useRef(`studio-preview-${(previewCount += 1)}`);
  return (
    <svg viewBox={ICON_VIEWBOX} width={size} height={size} fill="currentColor" aria-hidden="true">
      <Design described={described} idPrefix={id.current} />
    </svg>
  );
};

Preview.propTypes = {
  described: PropTypes.array.isRequired,
  size: PropTypes.number.isRequired,
};

const IconButton = ({ icon, label, onClick, disabled, children }) => (
  <button type="button" className={classes.tool} onClick={onClick} disabled={disabled} title={label} aria-label={children ? undefined : label}>
    {icon ? <UiIcon name={icon} /> : null}
    {children}
  </button>
);

IconButton.propTypes = {
  icon: PropTypes.string,
  label: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  children: PropTypes.node,
};

/**
 * A labelled slider with a value you can type and a reset. Dragging changes the design
 * live (gesture events make it one undo step); a typed value applies on Enter or blur.
 */
const ValueSlider = ({ id, label, min, max, step, value, unit, onChange, onType, onReset, resetLabel, isDefault, events }) => {
  const [draft, setDraft] = useState(String(value));
  const [typing, setTyping] = useState(false);
  useEffect(() => {
    if (!typing) setDraft(String(value));
  }, [value, typing]);
  const commit = () => {
    setTyping(false);
    const next = Number(draft);
    if (draft.trim() === "" || Number.isNaN(next)) return setDraft(String(value));
    const clamped = Math.min(max, Math.max(min, next));
    setDraft(String(clamped));
    if (clamped !== value) onType(clamped);
  };
  const fill = ((value - min) / (max - min)) * 100;
  return (
    <div className={classes.slider}>
      <label htmlFor={id} className={classes.sliderLabel}>
        {label}
      </label>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ "--fill": `${fill}%` }}
        onChange={(event) => onChange(Number(event.target.value))}
        onDoubleClick={isDefault ? undefined : onReset}
        {...events}
      />
      <span className={classes.sliderValue}>
        <input
          type="text"
          inputMode="numeric"
          aria-label={`${label} value`}
          value={draft}
          onFocus={(event) => {
            setTyping(true);
            event.target.select();
          }}
          onChange={(event) => setDraft(event.target.value.replace(/[^0-9.-]/g, ""))}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") event.target.blur();
            if (event.key === "Escape") {
              setDraft(String(value));
              setTyping(false);
              event.target.blur();
            }
          }}
        />
        <span aria-hidden="true">{unit}</span>
      </span>
      <button type="button" className={classes.sliderReset} title={resetLabel} aria-label={resetLabel} onClick={onReset} disabled={isDefault}>
        <UiIcon name="undo" />
      </button>
    </div>
  );
};

ValueSlider.propTypes = {
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
  unit: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onType: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired,
  resetLabel: PropTypes.string.isRequired,
  isDefault: PropTypes.bool,
  events: PropTypes.object,
};

// Small line icons for the transform buttons, drawn on a 20 grid
const TOOL_ICONS = {
  flipX: "M10 3v14M7.5 6 3 14h4.5zM12.5 6 17 14h-4.5z",
  flipY: "M3 10h14M6 7.5 14 3v4.5zM6 12.5l8 4.5v-4.5z",
  centre: "M10 2.5v3M10 14.5v3M2.5 10h3M14.5 10h3M10 13.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7z",
  circle: "M10 16a6 6 0 1 0 0-12 6 6 0 0 0 0 12z",
  square: "M4.5 4.5h11v11h-11z",
};

const ToolIcon = ({ name }) => (
  <svg className={classes.toolIcon} viewBox="0 0 20 20" aria-hidden="true" focusable="false">
    <path d={TOOL_ICONS[name]} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

ToolIcon.propTypes = { name: PropTypes.string.isRequired };

export const Studio = ({ icons, location }) => {
  const [doc, setDoc] = useState(EMPTY);
  const docRef = useRef(EMPTY);
  const past = useRef([]);
  const future = useRef([]);
  const [, setHistoryTick] = useState(0);
  // Selected layer ids; the parts of one icon are selected together until you double-click one
  const [selectedIds, setSelectedIds] = useState([]);
  const [tab, setTab] = useState("icons");
  const [query, setQuery] = useState("");
  const [done, setDone] = useState(null);
  const [problem, setProblem] = useState("");
  const [customIcons, setCustomIcons] = useState([]);
  const [ready, setReady] = useState(false);
  const svgRef = useRef(null);
  const drag = useRef(null);
  const doneTimer = useRef(null);

  // Icons by code (codes never change, so designs keep working) and duotone partners
  const lookup = useMemo(() => {
    const byCode = {};
    const byName = {};
    const secondaries = {};
    icons.forEach((icon) => {
      if (icon.assign === "after") {
        if (icon.isDuotone) secondaries[icon.name] = icon;
        return;
      }
      byCode[icon.code] = icon;
      if (!icon.isDuotone && !byName[icon.name]) byName[icon.name] = icon;
    });
    return { byCode, byName, secondaries };
  }, [icons]);
  const tiles = useMemo(() => icons.filter((icon) => icon.assign !== "after"), [icons]);

  const described = useMemo(() => describe(doc.layers, lookup), [doc.layers, lookup]);
  const selectedItems = described.filter((item) => selectedIds.indexOf(item.layer.id) !== -1);
  const selectionBox = unionBox(selectedItems);
  const single = selectedItems.length === 1 ? selectedItems[0] : null;
  const nameOk = SLUG.test(doc.name);
  const isEmpty = !described.some((item) => item.parts.length && item.layer.mode !== "cut");

  // History: every finished change can be undone; a drag counts as one change
  const set = (next, { record = true } = {}) => {
    if (record) {
      past.current.push(docRef.current);
      if (past.current.length > HISTORY_LIMIT) past.current.shift();
      future.current = [];
      setHistoryTick((tick) => tick + 1);
    }
    docRef.current = next;
    setDoc(next);
  };
  const undo = () => {
    if (!past.current.length) return;
    future.current.push(docRef.current);
    const previous = past.current.pop();
    docRef.current = previous;
    setDoc(previous);
    setHistoryTick((tick) => tick + 1);
  };
  const redo = () => {
    if (!future.current.length) return;
    past.current.push(docRef.current);
    const next = future.current.pop();
    docRef.current = next;
    setDoc(next);
    setHistoryTick((tick) => tick + 1);
  };

  const setLayers = (layers, options) => set({ ...docRef.current, layers }, options);
  const update = (id, patch, options) =>
    setLayers(docRef.current.layers.map((layer) => (layer.id === id ? { ...layer, ...patch } : layer)), options);

  // The draft is read once on load and kept as you work
  useEffect(() => {
    const draft = readDraft();
    docRef.current = draft;
    setDoc(draft);
    setCustomIcons(readCustomIcons());
    setReady(true);
    return onCustomIconsChange(setCustomIcons);
  }, []);

  useEffect(() => {
    if (ready) saveDraft(doc);
  }, [doc, ready]);

  // Path booleans (splitting icons, exports) need paper.js: start loading it right away
  useEffect(() => {
    loadPaper().catch(() => {});
  }, []);

  // An icon arrives split into its separate shapes, grouped, so each can be edited on its
  // own. Without paper.js (offline) it arrives whole
  const iconLayers = (icon, props) =>
    loadPaper().then(
      (paper) => {
        const whole = newLayer({ type: "icon", code: icon.code, ...props });
        const label = iconLabel(icon.name);
        const parts = splitLayer(describe([whole], lookup)[0], paper, "Part");
        if (parts.length < 2) return [{ ...(parts[0] || whole), label }];
        const group = newLayer().id;
        return parts.map((part) => ({ ...part, group, groupName: label }));
      },
      () => [newLayer({ type: "icon", code: icon.code, ...props })]
    );

  // ?icon=<code> (from an icon's panel) starts a new design from that icon; undo brings back the old one
  const linkRead = useRef(false);
  useEffect(() => {
    if (linkRead.current || !ready || !icons.length || !location || !location.search) return;
    const code = new URLSearchParams(location.search).get("icon");
    const icon = code && lookup.byCode[code];
    // The server preloads only the opening categories; wait for the rest if it isn't there yet
    if (!icon && icons.length < GLYPH_COUNT) return;
    linkRead.current = true;
    if (!icon) return;
    window.history.replaceState(window.history.state, "", window.location.pathname);
    iconLayers(icon).then((layers) => {
      set({ name: icon.name.slice(0, 32) + "-custom", layers });
      setSelectedIds(layers.map((layer) => layer.id));
    });
  }, [ready, icons.length]);

  useEffect(() => () => clearTimeout(doneTimer.current), []);

  const confirm = (id) => {
    setDone(id);
    setProblem("");
    clearTimeout(doneTimer.current);
    doneTimer.current = setTimeout(() => setDone(null), 1600);
  };

  const addLayers = (added, { atBottom = false } = {}) => {
    const layers = docRef.current.layers;
    setLayers(atBottom ? [...added, ...layers] : [...layers, ...added]);
    setSelectedIds(added.map((layer) => layer.id));
  };

  const addIcon = (icon) =>
    // The first icon fills the grid; later ones start at half size
    iconLayers(icon, { size: docRef.current.layers.length ? 0.5 : 1 }).then((layers) => addLayers(layers));

  const addShape = (shape) =>
    // Shapes are usually backgrounds, so they go under what's there
    addLayers([newLayer({ type: "shape", shape, tone: docRef.current.layers.length ? "light" : "solid" })], { atBottom: true });

  // Badges stay whole, so the space cleared around them is one circle
  const addBadge = (icon) =>
    addLayers([newLayer({ type: "icon", code: icon.code, size: 0.45, x: 6, y: 6, cutout: "circle", gap: 1 })]);

  // Selection helpers: a layer's whole group, and changes to everything selected
  const groupIds = (layer) =>
    layer.group
      ? docRef.current.layers.filter((item) => item.group === layer.group).map((item) => item.id)
      : [layer.id];

  const updateSelected = (patch) =>
    setLayers(docRef.current.layers.map((layer) => (selectedIds.indexOf(layer.id) !== -1 ? { ...layer, ...patch } : layer)));

  const transformSelected = (op, options) => {
    if (!selectionBox) return;
    const origin = { x: selectionBox.x + selectionBox.w / 2, y: selectionBox.y + selectionBox.h / 2 };
    setLayers(transformLayers(docRef.current.layers, selectedIds, op, origin), options);
  };

  // A slider drag changes the design live but undoes as one step
  const gesture = useRef(null);
  const beginGesture = () => {
    if (!gesture.current) gesture.current = docRef.current;
  };
  const endGesture = () => {
    const start = gesture.current;
    gesture.current = null;
    if (start && start !== docRef.current) {
      past.current.push(start);
      if (past.current.length > HISTORY_LIMIT) past.current.shift();
      future.current = [];
      setHistoryTick((tick) => tick + 1);
    }
  };

  // Moving layers between the Solid, Light and Hole zones
  const patchLayers = (ids, patch) =>
    setLayers(docRef.current.layers.map((layer) => (ids.indexOf(layer.id) !== -1 ? { ...layer, ...patch } : layer)));

  const centreSelected = () => {
    if (!selectionBox) return;
    transformSelected({ move: [12 - (selectionBox.x + selectionBox.w / 2), 12 - (selectionBox.y + selectionBox.h / 2)] });
  };

  const removeSelected = () => {
    setLayers(docRef.current.layers.filter((layer) => selectedIds.indexOf(layer.id) === -1));
    setSelectedIds([]);
  };

  // Copies sit one unit down and right; copied groups become new groups
  const duplicateSelected = () => {
    const groups = {};
    const copies = [];
    const layers = [];
    docRef.current.layers.forEach((layer) => {
      layers.push(layer);
      if (selectedIds.indexOf(layer.id) === -1) return;
      if (layer.group && !groups[layer.group]) groups[layer.group] = newLayer().id;
      const copy = { ...layer, id: newLayer().id, x: layer.x + 1, y: layer.y + 1, group: layer.group ? groups[layer.group] : undefined };
      copies.push(copy);
    });
    setLayers([...layers, ...copies]);
    setSelectedIds(copies.map((layer) => layer.id));
  };

  // Up or down one place, keeping the selected layers in their order
  const moveSelected = (step) => {
    const layers = [...docRef.current.layers];
    const order = layers.map((layer, index) => index);
    const indices = step > 0 ? order.reverse() : order;
    indices.forEach((index) => {
      const to = index + step;
      if (selectedIds.indexOf(layers[index].id) === -1 || to < 0 || to >= layers.length) return;
      if (selectedIds.indexOf(layers[to].id) !== -1) return;
      [layers[index], layers[to]] = [layers[to], layers[index]];
    });
    setLayers(layers);
  };

  const clearAll = () => {
    set({ ...docRef.current, layers: [] });
    setSelectedIds([]);
  };

  // Keyboard: arrows nudge (shift for 2 units), delete, duplicate, select all, undo / redo,
  // Esc deselects
  useEffect(() => {
    const onKey = (event) => {
      if (isTyping(event.target)) return;
      const mod = event.metaKey || event.ctrlKey;
      const key = event.key.toLowerCase();
      if (mod && key === "z") {
        event.preventDefault();
        if (event.shiftKey) redo();
        else undo();
        return;
      }
      if (mod && key === "y") {
        event.preventDefault();
        redo();
        return;
      }
      if (mod && key === "a") {
        event.preventDefault();
        setSelectedIds(docRef.current.layers.map((layer) => layer.id));
        return;
      }
      if (!selectedIds.length) return;
      if (mod && key === "d") {
        event.preventDefault();
        duplicateSelected();
      } else if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault();
        removeSelected();
      } else if (event.key === "Escape") {
        setSelectedIds([]);
      } else if (event.key.indexOf("Arrow") === 0) {
        event.preventDefault();
        const step = event.shiftKey ? 2 : 0.5;
        const dx = event.key === "ArrowLeft" ? -step : event.key === "ArrowRight" ? step : 0;
        const dy = event.key === "ArrowUp" ? -step : event.key === "ArrowDown" ? step : 0;
        transformSelected({ move: [dx, dy] });
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  // Pointer position in grid units
  const point = (event) => {
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = event.clientX;
    pt.y = event.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  // A click picks the whole icon (its group); shift-click adds or removes one part;
  // double-click picks just that part. Pressing on something selected drags the selection
  // (The canvas captures the pointer while dragging, so a double-click is spotted here:
  // a second press on the same layer within 400ms)
  const lastPress = useRef({ id: null, time: 0 });
  const pick = (event, item) => {
    const id = item.layer.id;
    const now = Date.now();
    const twice = lastPress.current.id === id && now - lastPress.current.time < 400;
    lastPress.current = { id, time: now };
    if (twice && !event.shiftKey) {
      setSelectedIds([id]);
      return [id];
    }
    if (event.shiftKey) {
      const next = selectedIds.indexOf(id) !== -1 ? selectedIds.filter((x) => x !== id) : [...selectedIds, id];
      setSelectedIds(next);
      return next;
    }
    if (selectedIds.indexOf(id) !== -1) return selectedIds;
    const next = groupIds(item.layer);
    setSelectedIds(next);
    return next;
  };

  const startDrag = (event, ids, mode) => {
    event.preventDefault();
    event.stopPropagation();
    if (!ids.length) return;
    const p = point(event);
    const box = unionBox(described.filter((item) => ids.indexOf(item.layer.id) !== -1));
    const ox = box ? box.x + box.w / 2 : 12;
    const oy = box ? box.y + box.h / 2 : 12;
    drag.current = {
      mode,
      ids,
      start: docRef.current,
      px: p.x,
      py: p.y,
      origin: { x: ox, y: oy },
      dist: Math.max(0.5, Math.hypot(p.x - ox, p.y - oy)),
    };
    svgRef.current.setPointerCapture(event.pointerId);
  };

  const onPointerMove = (event) => {
    const d = drag.current;
    if (!d) return;
    const p = point(event);
    // Hold Alt (Option) for free movement; otherwise moves snap to half units
    const free = event.altKey;
    const layers = d.start.layers;
    if (d.mode === "move") {
      const step = free ? 0.05 : 0.5;
      const dx = round(p.x - d.px, step);
      const dy = round(p.y - d.py, step);
      set({ ...docRef.current, layers: transformLayers(layers, d.ids, { move: [dx, dy] }) }, { record: false });
    } else {
      const sizes = layers.filter((layer) => d.ids.indexOf(layer.id) !== -1).map((layer) => layer.size);
      // Keep every layer between 5% and 300%
      const k = clamp(
        round(Math.hypot(p.x - d.origin.x, p.y - d.origin.y) / d.dist, free ? 0.01 : 0.05),
        0.05 / Math.min(...sizes),
        3 / Math.max(...sizes)
      );
      set({ ...docRef.current, layers: transformLayers(layers, d.ids, { scale: k }, d.origin) }, { record: false });
    }
  };

  const endDrag = () => {
    const d = drag.current;
    drag.current = null;
    if (d && d.start !== docRef.current) {
      past.current.push(d.start);
      if (past.current.length > HISTORY_LIMIT) past.current.shift();
      future.current = [];
      setHistoryTick((tick) => tick + 1);
    }
  };

  // Exports merge the layers into clean paths (paper.js loads the first time)
  const build = () =>
    loadPaper().then((paper) => {
      const flat = flatten(described, paper);
      const paths = [];
      if (flat.solid) paths.push({ d: flat.solid, opacity: 1 });
      if (flat.light) paths.push({ d: flat.light, opacity: DUOTONE_OPACITY });
      return { flat, shape: { name: nameOk ? doc.name : "my-icon", isDuotone: false, viewBox: ICON_VIEWBOX, paths } };
    });

  const runExport = (action) => {
    if (isEmpty) return;
    build().then(
      ({ shape }) => {
        if (action === "copy-svg") copyTextToClipboard(toSvg(shape));
        else if (action === "copy-jsx") copyTextToClipboard(toJsx(shape));
        else if (action === "copy-data-url") copyTextToClipboard(toDataUrl(shape));
        else if (action === "download-svg") downloadSvg(shape);
        else if (action === "download-png") return downloadPng(shape).then(() => confirm(action));
        confirm(action);
        return null;
      },
      () => setProblem("Couldn’t prepare the icon. Check your connection and try again.")
    );
  };

  const inFont = customIcons.some((icon) => icon.name === doc.name);
  const fontFull = !inFont && customIcons.length >= MAX_CUSTOM_ICONS;
  const takenBySet = nameOk && !!lookup.byName[doc.name];

  const addToFont = () => {
    if (isEmpty || !nameOk || fontFull) return;
    build().then(
      ({ flat }) => {
        saveCustomIcon({ name: doc.name, path: flat.solid || flat.light, pathSecondary: flat.solid ? flat.light || null : null });
        confirm("font");
      },
      () => setProblem("Couldn’t prepare the icon. Check your connection and try again.")
    );
  };

  const [styleFilter, setStyleFilter] = useState("all");
  const [showGrid, setShowGrid] = useState(true);
  const [dropZone, setDropZone] = useState(null);

  const results = useMemo(() => {
    const list = (query.trim() ? searchIcons(tiles, query) : tiles).filter(
      (icon) => styleFilter === "all" || iconStyle(icon) === styleFilter
    );
    return list.slice(0, ICON_RESULTS);
  }, [tiles, query, styleFilter]);

  const layerName = (layer) =>
    layer.type === "path"
      ? layer.label || "Part"
      : layer.type === "shape"
      ? SHAPES[layer.shape] ? SHAPES[layer.shape].label : "Shape"
      : lookup.byCode[layer.code] ? iconLabel(lookup.byCode[layer.code].name) : "Icon";

  // A layer's thumbnail shows just that layer, filling the square
  const thumbBox = (b) => {
    if (!b) return ICON_VIEWBOX;
    const side = Math.max(b.w, b.h, 2) * 1.15;
    return `${b.x + b.w / 2 - side / 2} ${b.y + b.h / 2 - side / 2} ${side} ${side}`;
  };

  // A setting all selected layers share, or null when they differ
  const shared = (key) => {
    const values = selectedItems.map((item) => item.layer[key]);
    return values.every((value) => value === values[0]) ? values[0] : null;
  };

  const layer = single ? single.layer : null;
  const box = selectionBox;
  // A selection that's exactly one icon's parts is named after the icon
  const selectedGroup =
    selectedItems.length > 1 &&
    selectedItems[0].layer.group &&
    selectedItems.every((item) => item.layer.group === selectedItems[0].layer.group) &&
    groupIds(selectedItems[0].layer).length === selectedItems.length
      ? selectedItems[0].layer.groupName
      : null;

  // Size and rotation sliders. One layer shows its own values. Several start at 100% / 0°
  // when they are selected and show how far they have changed since, read from the first
  // part, so undo and redo keep the values right
  const selectionKey = selectedIds.join(",");
  const firstSelected = selectedItems[0] ? selectedItems[0].layer : null;
  const baseline = useRef({ key: null, size: 1, rotate: 0 });
  if (baseline.current.key !== selectionKey && firstSelected) {
    baseline.current = { key: selectionKey, size: firstSelected.size, rotate: firstSelected.rotate };
  }
  const normalizeTurn = (degrees) => ((((degrees + 180) % 360) + 360) % 360) - 180;
  const sizeValue = layer
    ? Math.round(layer.size * 100)
    : firstSelected
    ? Math.round((firstSelected.size / baseline.current.size) * 100)
    : 100;
  const turnValue = Math.round(
    normalizeTurn(layer ? layer.rotate : firstSelected ? firstSelected.rotate - baseline.current.rotate : 0)
  );
  const setSize = (value) => {
    if (!sizeValue) return;
    transformSelected({ scale: value / sizeValue }, { record: false });
  };
  const setTurn = (value) => {
    transformSelected({ rotate: value - turnValue }, { record: false });
  };
  // A typed value or a reset is one undo step
  const asStep = (change) => (value) => {
    beginGesture();
    change(value);
    endGesture();
  };
  const resetSize = asStep(() => setSize(100));
  const resetTurn = asStep(() => setTurn(0));
  const sliderEvents = {
    onPointerDown: beginGesture,
    onKeyDown: beginGesture,
    onPointerUp: endGesture,
    onKeyUp: endGesture,
    onBlur: endGesture,
  };

  // The layer list: Solid, Light and Hole zones; dragging a layer between them changes it
  const ZONES = [
    { id: "solid", label: "Solid", hint: "Drawn at full strength", has: (l) => l.mode !== "cut" && l.tone !== "light", patch: { mode: "add", tone: "solid" } },
    { id: "light", label: "Light · 60%", hint: "The duotone tone", has: (l) => l.mode !== "cut" && l.tone === "light", patch: { mode: "add", tone: "light" } },
    { id: "hole", label: "Hole", hint: "Cuts its shape out of the layers below", has: (l) => l.mode === "cut", patch: { mode: "cut" } },
  ];
  const zoneOf = (l) => ZONES.find((zone) => zone.has(l)).id;
  const dragIds = (id) => (selectedIds.indexOf(id) !== -1 ? selectedIds : [id]);

  const layerRow = (item) => {
    const on = selectedIds.indexOf(item.layer.id) !== -1;
    return (
      <li key={item.layer.id}>
        <button
          type="button"
          draggable
          onDragStart={(event) => {
            event.dataTransfer.setData("text/plain", item.layer.id);
            event.dataTransfer.effectAllowed = "move";
          }}
          className={on ? classes.layerOn : classes.layer}
          onClick={(event) =>
            setSelectedIds(
              event.shiftKey
                ? on
                  ? selectedIds.filter((id) => id !== item.layer.id)
                  : [...selectedIds, item.layer.id]
                : [item.layer.id]
            )
          }
          onDoubleClick={() => setSelectedIds(groupIds(item.layer))}
          aria-pressed={on}
          title="Drag to another zone · double-click to select the whole icon"
        >
          <svg viewBox={thumbBox(item.box)} aria-hidden="true">
            {item.parts.map((part, i) => (
              <path key={i} d={part.d} />
            ))}
          </svg>
          <span>
            {item.layer.groupName ? <small>{item.layer.groupName} · </small> : null}
            {layerName(item.layer)}
          </span>
        </button>
      </li>
    );
  };

  const tone = layer ? zoneOf(layer) : selectedItems.length ? (selectedItems.every((item) => zoneOf(item.layer) === zoneOf(selectedItems[0].layer)) ? zoneOf(selectedItems[0].layer) : null) : null;

  const PICKER_TABS = [
    { id: "icons", label: "Icons", icon: "search" },
    { id: "badges", label: "Badges", icon: "plus-circle" },
    { id: "shapes", label: "Shapes", icon: "layers" },
  ];

  return (
    <div>
      <Helmet>
        <title>Icon Studio: Design Your Own SVG Icons | IcoFont</title>
        <meta
          name="description"
          content="Combine IcoFont icons, shapes and badges into your own icons on a 24px grid, then copy or download them as SVG, PNG or JSX, or add them to your icon font."
        />
      </Helmet>
      <PageHeader label="Studio" title="Design your own icons" />

      <div className={classes.page}>
        {/* Top bar: history and view on the left, name and exports on the right */}
        <div className={classes.topbar}>
          <div className={classes.tools}>
            <IconButton icon="undo" label="Undo (⌘Z)" onClick={undo} disabled={!past.current.length} />
            <IconButton icon="redo" label="Redo (⇧⌘Z)" onClick={redo} disabled={!future.current.length} />
            <button
              type="button"
              className={`${classes.tool} ${showGrid ? classes.toolOn : ""}`}
              onClick={() => setShowGrid(!showGrid)}
              aria-pressed={showGrid}
              title="Show the 24px grid and keylines"
            >
              Grid
            </button>
            <IconButton label="Start over" onClick={clearAll} disabled={!doc.layers.length}>
              Clear
            </IconButton>
          </div>
          <div className={classes.exports}>
            <input
              type="text"
              className={classes.name}
              value={doc.name}
              maxLength={40}
              spellCheck="false"
              aria-label="Icon name"
              aria-invalid={!nameOk}
              onChange={(event) => set({ ...docRef.current, name: toSlug(event.target.value) }, { record: false })}
            />
            <div className={classes.split}>
              <SplitButton
                options={EXPORTS}
                storageKey="icofont:studio-export"
                label="More export options"
                onSelect={runExport}
                doneId={done}
                doneLabel={done && done.indexOf("download") === 0 ? "Downloaded" : "Copied"}
                disabled={isEmpty}
              />
            </div>
            <button
              type="button"
              className={`detail-btn${done === "font" ? " is-done" : inFont ? " is-added" : ""}`}
              onClick={addToFont}
              disabled={isEmpty || !nameOk || fontFull}
              title={fontFull ? `Fonts take up to ${MAX_CUSTOM_ICONS} custom icons` : undefined}
            >
              {done === "font" ? (
                <React.Fragment>
                  <CheckMark /> In your font
                </React.Fragment>
              ) : (
                <React.Fragment>
                  <UiIcon name={inFont ? "check" : "plus"} />
                  {inFont ? "Update in font" : "Add to font"}
                </React.Fragment>
              )}
            </button>
          </div>
        </div>
        {!nameOk || takenBySet || problem || customIcons.length ? (
          <div className={classes.notes}>
            {!nameOk ? <p className={classes.problem}>Name: start with a letter; use lowercase letters, numbers and hyphens.</p> : null}
            {nameOk && takenBySet ? (
              <p className={classes.muted}>IcoFont also has a “{doc.name}” icon. Rename yours to use both in one font.</p>
            ) : null}
            {problem ? (
              <p className={classes.problem} role="alert">
                {problem}
              </p>
            ) : null}
            {customIcons.length ? (
              <p className={classes.muted}>
                {customIcons.length} custom {customIcons.length === 1 ? "icon" : "icons"} in your font ·{" "}
                <Link to={COMPOSER_PATH}>Open the composer</Link>
              </p>
            ) : null}
          </div>
        ) : null}

        <div className={classes.editor}>
          {/* Picker */}
          <section className={classes.picker} aria-label="Add to your icon">
            <div className={classes.pickerTabs} role="tablist" aria-label="Add">
              {PICKER_TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.id}
                  onClick={() => setTab(item.id)}
                >
                  <UiIcon name={item.icon} />
                  {item.label}
                </button>
              ))}
            </div>
            <div className={classes.pickerBody}>
              {tab === "icons" ? (
                <React.Fragment>
                  <div className={classes.search}>
                    <UiIcon name="search" />
                    <input
                      type="search"
                      value={query}
                      placeholder="Search icons"
                      aria-label="Search icons"
                      autoComplete="off"
                      spellCheck="false"
                      onChange={(event) => setQuery(event.target.value)}
                    />
                    <select value={styleFilter} onChange={(event) => setStyleFilter(event.target.value)} aria-label="Style">
                      <option value="all">All</option>
                      <option value="line">Line</option>
                      <option value="solid">Solid</option>
                      <option value="duotone">Duotone</option>
                    </select>
                  </div>
                  {!tiles.length ? (
                    <p className={classes.muted}>Loading icons…</p>
                  ) : results.length ? (
                    <ul className={classes.grid}>
                      {results.map((icon) => (
                        <li key={icon.id}>
                          <button type="button" title={`Add ${iconLabel(icon.name)}`} onClick={() => addIcon(icon)}>
                            <Svg
                              path={gridPath(icon.svg)}
                              pathSecondary={lookup.secondaries[icon.name] && icon.isDuotone ? gridPath(lookup.secondaries[icon.name].svg) : null}
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className={classes.muted}>No icons match.</p>
                  )}
                </React.Fragment>
              ) : tab === "badges" ? (
                <React.Fragment>
                  <p className={classes.muted}>Added small in the bottom corner, with space cut around them.</p>
                  <ul className={classes.grid}>
                    {BADGES.map((name) => lookup.byName[name]).filter(Boolean).map((icon) => (
                      <li key={icon.id}>
                        <button type="button" title={`Add ${iconLabel(icon.name)} badge`} onClick={() => addBadge(icon)}>
                          <Svg path={gridPath(icon.svg)} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </React.Fragment>
              ) : (
                <ul className={classes.grid}>
                  {Object.keys(SHAPES).map((key) => (
                    <li key={key}>
                      <button type="button" title={`Add ${SHAPES[key].label.toLowerCase()}`} onClick={() => addShape(key)}>
                        <svg className="glyph" viewBox={ICON_VIEWBOX} aria-hidden="true">
                          <path d={SHAPES[key].d} />
                        </svg>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className={classes.previews} aria-label="Previews">
              {[16, 24, 32, 48, 64].map((size) => (
                <Preview key={size} described={described} size={size} />
              ))}
            </div>
          </section>

          {/* Canvas */}
          <section className={classes.stage} aria-label="Canvas">
            <svg
              ref={svgRef}
              viewBox="-1.5 -1.5 27 27"
              className={classes.canvasSvg}
              onPointerDown={() => setSelectedIds([])}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
              role="img"
              aria-label={`Your icon, ${doc.layers.length} ${doc.layers.length === 1 ? "layer" : "layers"}`}
            >
              <defs>
                <pattern id="studio-checker" width="2" height="2" patternUnits="userSpaceOnUse">
                  <rect width="2" height="2" fill="#fff" />
                  <rect width="1" height="1" fill="#f0eef4" />
                  <rect x="1" y="1" width="1" height="1" fill="#f0eef4" />
                </pattern>
              </defs>
              {/* The grid on a plain canvas; with the grid off, a checkerboard shows what's see-through */}
              <rect x="0" y="0" width="24" height="24" fill={showGrid ? "#fff" : "url(#studio-checker)"} />
              {showGrid ? (
                <React.Fragment>
                  <g className={classes.gridLines} aria-hidden="true">
                    {Array.from({ length: 23 }, (_, i) => (
                      <React.Fragment key={i}>
                        <line x1={i + 1} y1="0" x2={i + 1} y2="24" />
                        <line x1="0" y1={i + 1} x2="24" y2={i + 1} />
                      </React.Fragment>
                    ))}
                  </g>
                  <g className={classes.keylines} aria-hidden="true">
                    <rect x="2" y="2" width="20" height="20" rx="2" />
                    <circle cx="12" cy="12" r="10" />
                  </g>
                </React.Fragment>
              ) : null}
              <Design described={described} idPrefix="studio-canvas" editor />
              {/* Outside the 24 grid isn't exported */}
              <path className={classes.outside} d="M-1.5-1.5h27v27h-27zM0 0v24h24v-24z" fillRule="evenodd" />
              {/* The selection can be dragged anywhere in its box... */}
              {box ? (
                <rect
                  x={box.x}
                  y={box.y}
                  width={Math.max(box.w, 0.5)}
                  height={Math.max(box.h, 0.5)}
                  className={classes.grab}
                  onPointerDown={(event) => startDrag(event, selectedIds, "move")}
                />
              ) : null}
              {/* ...and every layer by its own shape, top layer last so it wins */}
              {described.map((item) =>
                item.parts.map((part, i) => (
                  <path
                    key={`${item.layer.id}-${i}`}
                    d={part.d}
                    className={classes.grabShape}
                    onPointerDown={(event) => startDrag(event, pick(event, item), "move")}
                  />
                ))
              )}
              {box ? (
                <g>
                  <rect x={box.x} y={box.y} width={box.w} height={box.h} className={classes.selection} />
                  <rect
                    x={box.x + box.w - 0.5}
                    y={box.y + box.h - 0.5}
                    width="1"
                    height="1"
                    rx="0.25"
                    className={classes.handle}
                    onPointerDown={(event) => startDrag(event, selectedIds, "scale")}
                  >
                    <title>Drag to resize</title>
                  </rect>
                </g>
              ) : null}
            </svg>
            {!doc.layers.length ? (
              <div className={classes.emptyHint}>
                <UiIcon name="magic" />
                <p>Pick an icon, badge or shape to start.</p>
              </div>
            ) : null}
            <p className={classes.hint}>
              Click picks an icon · double-click one part · drag to move
              <span className={classes.hintPointer}> · Alt for fine moves</span>
            </p>
          </section>

          {/* Inspector: layers by tone, then the selection's controls */}
          <aside className={classes.inspector} aria-label="Layers and settings">
            <div className={classes.zones}>
              {ZONES.map((zone) => {
                const items = [...described].reverse().filter((item) => zone.has(item.layer));
                return (
                  <section
                    key={zone.id}
                    className={`${classes.zone} ${dropZone === zone.id ? classes.zoneOver : ""}`}
                    aria-label={zone.label}
                    onDragOver={(event) => {
                      event.preventDefault();
                      setDropZone(zone.id);
                    }}
                    onDragLeave={() => setDropZone(null)}
                    onDrop={(event) => {
                      event.preventDefault();
                      setDropZone(null);
                      const id = event.dataTransfer.getData("text/plain");
                      if (id) patchLayers(dragIds(id), zone.patch);
                    }}
                  >
                    <h3 title={zone.hint}>{zone.label}</h3>
                    {items.length ? (
                      <ol className={classes.layers}>{items.map((item) => layerRow(item))}</ol>
                    ) : (
                      <p className={classes.dropHint}>Drag a layer here</p>
                    )}
                  </section>
                );
              })}
            </div>

            {selectedItems.length ? (
              <div className={classes.controls}>
                <div className={classes.controlsHead}>
                  <strong>
                    {layer ? layerName(layer) : selectedGroup || `${selectedItems.length} parts`}
                    {/* A duotone icon's parts draw in both tones, so no single tone is on */}
                    {tone ? null : <span className={classes.mixed}>Mixed</span>}
                  </strong>
                  <div className="segmented" role="group" aria-label="Draws as">
                    {ZONES.map((zone) => (
                      <button
                        key={zone.id}
                        type="button"
                        aria-pressed={tone === zone.id}
                        title={zone.hint}
                        onClick={() => patchLayers(selectedIds, zone.patch)}
                      >
                        {zone.id === "light" ? "Light" : zone.label}
                      </button>
                    ))}
                  </div>
                </div>

                <ValueSlider
                  id="studio-size"
                  label="Size"
                  min={5}
                  max={300}
                  step={1}
                  value={sizeValue}
                  unit="%"
                  onChange={setSize}
                  onType={asStep(setSize)}
                  onReset={resetSize}
                  resetLabel="Back to 100%"
                  isDefault={sizeValue === 100}
                  events={sliderEvents}
                />
                <ValueSlider
                  id="studio-rotate"
                  label="Rotate"
                  min={-180}
                  max={180}
                  step={1}
                  value={turnValue}
                  unit="°"
                  onChange={setTurn}
                  onType={asStep(setTurn)}
                  onReset={resetTurn}
                  resetLabel="Back to 0°"
                  isDefault={turnValue === 0}
                  events={sliderEvents}
                />

                <div className={classes.actions}>
                  <span className={classes.actionsLabel}>Transform</span>
                  <div className={classes.actionsRow}>
                    <div className={classes.group}>
                      <button type="button" title="Flip horizontally" aria-label="Flip horizontally" onClick={() => transformSelected({ flip: "x" })}>
                        <ToolIcon name="flipX" />
                      </button>
                      <button type="button" title="Flip vertically" aria-label="Flip vertically" onClick={() => transformSelected({ flip: "y" })}>
                        <ToolIcon name="flipY" />
                      </button>
                    </div>
                    <div className={classes.group}>
                      <button type="button" title="Centre on the grid" aria-label="Centre on the grid" onClick={centreSelected}>
                        <ToolIcon name="centre" />
                      </button>
                    </div>
                    {layer && layer.mode !== "cut" ? (
                      <div className={classes.group}>
                        <button
                          type="button"
                          className={layer.cutout !== "none" ? classes.buttonOn : undefined}
                          aria-pressed={layer.cutout !== "none"}
                          title={`Space around: ${layer.cutout === "none" ? "off" : layer.cutout} (click to change)`}
                          aria-label="Space around this layer"
                          onClick={() =>
                            update(layer.id, { cutout: layer.cutout === "none" ? "circle" : layer.cutout === "circle" ? "square" : "none" })
                          }
                        >
                          <ToolIcon name={layer.cutout === "square" ? "square" : "circle"} />
                        </button>
                      </div>
                    ) : null}
                  </div>

                  {layer && layer.mode !== "cut" && layer.cutout !== "none" ? (
                    <ValueSlider
                      id="studio-gap"
                      label="Gap"
                      min={0}
                      max={4}
                      step={0.5}
                      value={layer.gap}
                      unit=""
                      onChange={(value) => update(layer.id, { gap: value }, { record: false })}
                      onType={(value) => update(layer.id, { gap: value })}
                      onReset={() => update(layer.id, { gap: newLayer({}).gap })}
                      resetLabel="Back to the default gap"
                      isDefault={layer.gap === newLayer({}).gap}
                      events={sliderEvents}
                    />
                  ) : null}

                  <span className={classes.actionsLabel}>Layer</span>
                  <div className={classes.actionsRow}>
                    <div className={classes.group}>
                      <button type="button" title="Bring forward" aria-label="Bring forward" onClick={() => moveSelected(1)}>
                        <UiIcon name="arrow-up" />
                      </button>
                      <button type="button" title="Send backward" aria-label="Send backward" onClick={() => moveSelected(-1)}>
                        <UiIcon name="arrow-down" />
                      </button>
                    </div>
                    <div className={classes.group}>
                      <button type="button" title="Duplicate (⌘D)" aria-label="Duplicate" onClick={duplicateSelected}>
                        <UiIcon name="ui-copy" />
                      </button>
                    </div>
                    <div className={classes.group}>
                      <button type="button" className={classes.danger} title="Delete (⌫)" aria-label="Delete" onClick={removeSelected}>
                        <UiIcon name="ui-delete" />
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            ) : (
              <p className={classes.selectHint}>{doc.layers.length ? "Select a layer to edit it." : "Nothing here yet."}</p>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
};

Studio.propTypes = {
  icons: PropTypes.array.isRequired,
  location: PropTypes.object,
};

export default Studio;
